import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LogOut,
  BarChart3,
  FolderOpen,
  BookOpen,
  GraduationCap,
  Users,
  FileText,
  FileUser,
  HelpCircle,
  Briefcase,
  Star,
  Settings,
  Layers,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Tag,
  Ticket
} from "lucide-react";

const cx = (...c) => c.filter(Boolean).join(" ");

export default function Sidebar() {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
      if (window.innerWidth < 1024) {
        setIsCollapsed(false); // Always expanded on mobile when open
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/", { replace: true });
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const navItems = [
    { to: "/dashboard", icon: BarChart3, label: "Dashboard" },
    { to: "/categories", icon: FolderOpen, label: "Categories" },
    { to: "/course-categories", icon: Layers, label: "Course Categories" },
    { to: "/courses", icon: BookOpen, label: "Courses" },
    // { to: "/program-categories", icon: Briefcase, label: "Program Categories" },
    // { to: "/departments", icon: GraduationCap, label: "Departments" },
    { to: "/subscription-plans", icon: GraduationCap, label: "Subscription Plans" },
    { to: "/subscribed-users", icon: GraduationCap, label: "Doc Subscribed Users" },
    { to: "/resume-subscribed-users", icon: GraduationCap, label: "Res Subscribed Users" },
    // { to: "/programs", icon: GraduationCap, label: "Programs" },
    { to: "/lecturers", icon: Users, label: "Lecturers" },
    { to: "/users", icon: Users, label: "Users" },
    { to: "/documents", icon: FileText, label: "Course Documents" },
    // { to: "/resume", icon: FileUser, label: "Resume Management" },
    { to: "/reviews-ratings", icon: Star, label: "Reviews & Ratings" },
    { to: "/faqs", icon: HelpCircle, label: "FAQs" },
    { to: "/support-categories", icon: Tag, label: "Support Categories" },
    { to: "/support-tickets", icon: Ticket, label: "Support Tickets" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed top-4 left-4 z-50 p-3 bg-red-600 text-white rounded-2xl shadow-lg lg:hidden hover:bg-red-700 transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}

      {/* Mobile Backdrop */}
      {isMobile && isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar */}
<aside
  className={cx(
    "fixed left-0 top-0 h-screen flex flex-col shadow-lg z-50 transition-all duration-300 ease-in-out",
    "bg-white ring-1 ring-gray-200 overflow-x-hidden",
    isMobile
      ? `w-64 transform ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`
      : `${isCollapsed ? 'w-16' : 'w-64'}`
  )}
>

        {/* Header */}
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {(!isCollapsed || isMobile) && (
              <h2 className="text-xl font-extrabold text-red-600 truncate">
                R-Ed Admin
              </h2>
            )}
            {!isMobile && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-2xl hover:bg-gray-50 transition-colors flex-shrink-0 ml-auto ring-1 ring-gray-200"
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <ChevronRight size={18} className="text-gray-600" />
                ) : (
                  <ChevronLeft size={18} className="text-gray-600" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
<nav className="flex-1 p-4 space-y-2 no-scrollbar">

          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <div key={item.to} className="relative group">
                <NavLink
                  to={item.to}
                  onClick={closeMobileSidebar}
                  className={({ isActive }) =>
                    cx(
                      "flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-extrabold transition-all duration-200 ring-1",
                      isActive
                        ? "bg-red-600 text-white ring-red-600 shadow-md"
                        : "text-gray-700 hover:bg-gray-50 hover:text-red-600 ring-gray-200",
                      !isMobile && isCollapsed ? "justify-center" : ""
                    )
                  }
                >
                  <IconComponent size={20} className="flex-shrink-0" />
                  {(isMobile || !isCollapsed) && (
                    <span className="truncate">{item.label}</span>
                  )}
                </NavLink>

                {/* Tooltip for collapsed state (desktop only) */}
                {!isMobile && isCollapsed && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none shadow-lg">
                    {item.label}
                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout button */}
        <div className="p-4 border-t border-gray-200">
          <div className="relative group">
            <button
              onClick={() => setShowLogoutModal(true)}
              className={cx(
                "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-extrabold text-red-600 hover:bg-red-50 transition-all duration-200 ring-1 ring-gray-200",
                !isMobile && isCollapsed ? "justify-center" : ""
              )}
            >
              <LogOut size={20} className="flex-shrink-0" />
              {(isMobile || !isCollapsed) && <span className="truncate">Logout</span>}
            </button>

            {/* Tooltip for collapsed logout button (desktop only) */}
            {!isMobile && isCollapsed && (
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none shadow-lg">
                Logout
                <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowLogoutModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 ring-1 ring-gray-200">
            <h3 className="text-xl font-extrabold text-gray-900">
              Confirm Logout
            </h3>

            <p className="mt-3 text-sm text-gray-600 leading-6">
              Are you sure you want to logout from your account? You'll need to sign in again to access the admin portal.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-3 rounded-2xl text-sm font-extrabold text-gray-700 hover:bg-gray-50 transition-colors ring-1 ring-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  handleLogout();
                  setShowLogoutModal(false);
                  closeMobileSidebar();
                }}
                className="flex-1 px-4 py-3 rounded-2xl text-sm font-extrabold text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
